/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import {AutoFocusPlugin} from '@lexical/react/LexicalAutoFocusPlugin';
import {ClearEditorPlugin} from '@lexical/react/LexicalClearEditorPlugin';
import {HashtagPlugin} from '@lexical/react/LexicalHashtagPlugin';
import {LinkPlugin} from '@lexical/react/LexicalLinkPlugin';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import ActionsPlugin from './plugins/ActionsPlugin';
import AutoEmbedPlugin from './plugins/AutoEmbedPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin';
import ClickableLinkPlugin from './plugins/ClickableLinkPlugin';
import ComponentPickerPlugin from './plugins/ComponentPickerPlugin';
import KeywordsPlugin from './plugins/KeywordsPlugin';
import {MaxLengthPlugin} from './plugins/MaxLengthPlugin';
import MentionsPlugin from './plugins/MentionsPlugin';
import TabFocusPlugin from './plugins/TabFocusPlugin'; 
import ToolbarPlugin from './plugins/ToolbarPlugin';
import ContentEditable from './ui/ContentEditable';
import Placeholder from './ui/Placeholder';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {$generateHtmlFromNodes} from '@lexical/html';
import {$getRoot, $getSelection} from 'lexical';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import { useEffect } from 'react';

const defaultContent = {
  root: {
    children: [
      {
        children:[],
        direction:null,
        format:"",
        indent:0,
        type:"paragraph",
        version:1
      }
    ],
    direction:null,
    format:"",
    indent:0,
    type:"root",
    version:1
  },
};

export default function Editor(props: any): JSX.Element {

  const {
    placeholder = 'Descripción',
    editable = true,
    clear = false,
    setClear = () => {},
  } = props;

  const [editor] = useLexicalComposerContext();
  
  //IF CONTENT ARRAY IS EMPTY, SET INITIAL CONTENT
  useEffect (() => {
    let jsonContent;
    if(props.content){
      jsonContent = JSON.stringify(props.content);
    } else {
      jsonContent = JSON.stringify(defaultContent);
    }
    const editorState = editor.parseEditorState(jsonContent);
    editor.setEditorState(editorState);
  }, [props.content]);

  //console.log("json", props.content);
  
  //console.log("jsonContent3", jsonContent);

  useEffect (() => {
    editor.setEditable(editable);
  }, [editable]);
  
  useEffect (() => {
    if (clear) {      
      const jsonContent = JSON.stringify(defaultContent);
      const editorState = editor.parseEditorState(jsonContent);
      editor.setEditorState(editorState);

      setClear(false);
    }
  }, [clear]);
  
  function onChange(editorState: { read: (arg0: () => void) => void; }) {
    
    editorState.read(() => {
      // Read the contents of the EditorState here.
      //const jsonContent = $getRoot();
      //const selection = $getSelection();
      const htmlContent = $generateHtmlFromNodes(editor);
      
      const editorState = editor.getEditorState();
      const jsonContent = editorState.toJSON();

      props.handleEditorChange(htmlContent, jsonContent);
    });
  }


  
  return (
    <>
      <ToolbarPlugin />
      <div
        className='editor-container tree-view'>
        <AutoFocusPlugin />
        <ClearEditorPlugin />
        <ComponentPickerPlugin />
        <AutoEmbedPlugin />
        <MentionsPlugin />
        <HashtagPlugin />
        <KeywordsPlugin />
        <AutoLinkPlugin />

        <RichTextPlugin
          contentEditable={
            <div className="editor-scroller">
              <div className="editor" >
                <ContentEditable />
              </div> 
            </div>
          }
          placeholder={<Placeholder>{placeholder}</Placeholder>}
        />
        <ListPlugin /> 
        <LinkPlugin />
        <OnChangePlugin onChange={onChange} />
        <ClickableLinkPlugin />
        <TabFocusPlugin />
        <ActionsPlugin isRichText={true} />
      </div>
    </>
  );
}
